/**
 * objGroups.js — the polygroup names inside an OBJ, read without loading it whole.
 *
 * WHY THIS EXISTS
 *
 * The team separates a printed part from the anatomy it sits on by POLYGROUP, in
 * one file: `Page 20 - Genioplasty fixation marking guide.OBJ` is the guide
 * (`Group32764`), the trial-fitment mandible (`Group39469`) and the screw inserts
 * (`Group16131`). Colouring the file as one thing can only make all of it right
 * or all of it wrong — it made the anatomy white. So the operator needs to see
 * the groups, which means reading them out of the file.
 *
 * WHY CHUNKED
 *
 * These files are large: the corpus skull is 125 MB and the splint 57 MB.
 * `file.text()` would hold the whole decoded string AND its split, spiking a few
 * hundred MB in a browser tab that is also holding three.js geometry. `g`
 * statements are scattered throughout, so a partial read cannot be trusted to
 * find them all — but a streamed scan can, at bounded memory.
 *
 * Results are memoised on (name, size, lastModified) — the SAME fingerprint the
 * render cache uses, and for the same reason: two cases hold identically named
 * meshes, because the team names by page rather than by patient.
 */

const cache = new Map()
const fingerprint = (file) => `${file.name}:${file.size}:${file.lastModified}`

/**
 * @param {File|Blob} file
 * @returns {Promise<string[]>} group names in file order, deduped. Empty for a
 *   file with no `g` statements (an STL, or an OBJ the exporter did not group).
 */
export async function readPolygroups(file) {
  if (!file || typeof file.slice !== 'function') return []
  // ONLY OBJs. `g` is an OBJ statement; an STL has no polygroups at all. Without
  // this a 125 MB binary STL was decoded in full to answer "one part", and its
  // random vertex bytes matched `\ng ` often enough to invent group names that
  // no mesh is ever called — offering the operator dropdowns that cannot work.
  if (!/\.obj$/i.test(file.name ?? '')) return []
  const key = fingerprint(file)
  if (cache.has(key)) return cache.get(key)

  const CHUNK = 4 * 1024 * 1024
  const decoder = new TextDecoder()
  const names = []
  const seen = new Set()
  let tail = ''

  for (let at = 0; at < file.size; at += CHUNK) {
    const buf = await file.slice(at, Math.min(at + CHUNK, file.size)).arrayBuffer()
    // stream: true so a multi-byte character split across the boundary is not
    // mangled into a replacement char mid-name.
    const text = tail + decoder.decode(buf, { stream: true })
    const lines = text.split('\n')
    // The last line may be incomplete — carry it to the next chunk rather than
    // reading a truncated group name as a real one.
    tail = lines.pop() ?? ''
    for (const line of lines) {
      if (line.charCodeAt(0) !== 103) continue          // 'g'
      if (line[1] !== ' ' && line[1] !== '\t') continue
      const name = line.slice(2).trim()
      if (name && !seen.has(name)) { seen.add(name); names.push(name) }
    }
  }
  const last = tail.trim()
  if (last.startsWith('g ') || last.startsWith('g\t')) {
    const name = last.slice(2).trim()
    if (name && !seen.has(name)) names.push(name)
  }

  cache.set(key, names)
  return names
}

/** Testing seam — the cache is keyed on file identity and would outlive a case. */
export const __clearPolygroupCache = () => cache.clear()
